import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { MapPin, CaretLeft, CaretRight, NavigationArrow, CircleNotch } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import ProviderCard from "../components/ProviderCard";
import Breadcrumb from "../components/Breadcrumb";
import { richItemListSchema } from "../lib/jsonLd";
import { api } from "../lib/api";

const POPULAR_SPECIALTIES = [
  "All Specialties",
  "Family Medicine",
  "Internal Medicine",
  "Cardiology",
  "Dermatology",
  "Pediatrics",
  "Psychiatry",
  "Orthopedics",
  "Neurology"
];

const RADIUS_OPTIONS = [
  { value: "5", label: "5 miles", prefix: 5 },
  { value: "10", label: "10 miles", prefix: 4 },
  { value: "25", label: "25 miles", prefix: 3 }
];

const ITEMS_PER_PAGE = 20;

const NearMePage = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Support both URL params and query params for backwards compatibility
  const postalCode = params.postalCode || searchParams.get("postal_code");
  const lat = searchParams.get("lat");
  const lng = searchParams.get("lng");
  const initialRadius = params.radius || searchParams.get("radius") || "10";
  
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [specialtyFilter, setSpecialtyFilter] = useState("All Specialties");
  const [radiusFilter, setRadiusFilter] = useState(initialRadius);
  const [currentPage, setCurrentPage] = useState(1);
  const [locationName, setLocationName] = useState("");
  
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  // Get postal code prefix based on radius
  const getPostalCodeForRadius = (fullPostalCode, radius) => {
    if (!fullPostalCode) return "";
    const clean = fullPostalCode.replace(/[^0-9]/g, '').substring(0, 5);
    const option = RADIUS_OPTIONS.find(r => r.value === radius);
    const prefixLength = option ? option.prefix : 4;
    return clean.substring(0, prefixLength);
  };

  // Get location name from coordinates
  useEffect(() => {
    const fetchLocationName = async () => {
      if (lat && lng) {
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10`,
            { headers: { 'User-Agent': 'NPI-Lookup-App' } }
          );
          const data = await response.json();
          const city = data.address?.city || data.address?.town || data.address?.village || "";
          const state = data.address?.state || "";
          setLocationName(city && state ? `${city}, ${state}` : postalCode);
        } catch (error) {
          setLocationName(postalCode);
        }
      } else {
        setLocationName(postalCode);
      }
    };
    fetchLocationName();
  }, [lat, lng, postalCode]);

  const fetchResults = useCallback(async () => {
    if (!postalCode) return;
    
    setLoading(true);
    try {
      // Use postal code prefix based on radius for wider search
      const searchPostalCode = getPostalCodeForRadius(postalCode, radiusFilter);
      
      const params = {
        postal_code: searchPostalCode,
        limit: ITEMS_PER_PAGE,
        skip: (currentPage - 1) * ITEMS_PER_PAGE
      };
      
      if (specialtyFilter && specialtyFilter !== "All Specialties") {
        params.taxonomy_description = specialtyFilter;
      }
      
      const data = await api.searchProviders(params);
      setResults(data.results || []);
      setTotalCount(data.result_count || 0);
    } catch (error) {
      console.error("Error fetching nearby providers:", error);
      setResults([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [postalCode, specialtyFilter, radiusFilter, currentPage]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  const handleSpecialtyChange = (value) => {
    setSpecialtyFilter(value);
    setCurrentPage(1);
  };

  const handleRadiusChange = (value) => {
    setRadiusFilter(value);
    setCurrentPage(1);
    // Navigate to clean URL
    navigate(`/near-me/${postalCode}/${value}`);
  };

  if (!postalCode) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
          <MapPin className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h1 className="font-display font-bold text-2xl text-slate-900 mb-2">
            Location Required
          </h1>
          <p className="text-slate-500 mb-6">
            Please use the "Find providers near me" button on the homepage to search by location.
          </p>
          <Link to="/">
            <Button className="bg-navy hover:bg-navy/90">
              Go to Homepage
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const currentRadiusLabel = RADIUS_OPTIONS.find(r => r.value === radiusFilter)?.label || "10 miles";
  const pageTitle = "Healthcare Providers Near " + (locationName || postalCode) + " | NPI Lookup";
  const pageDescription = "Find " + totalCount.toLocaleString() + "+ healthcare providers within " + currentRadiusLabel + " of " + (locationName || "ZIP " + postalCode) + ". Browse verified NPI numbers and contact information.";

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={`https://npilookup.com/near-me/${postalCode}`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalWebPage",
          name: pageTitle,
          description: pageDescription,
          url: `https://npilookup.com/near-me/${postalCode}`,
          about: {
            "@type": "Place",
            name: locationName || `ZIP ${postalCode}`,
            address: { "@type": "PostalAddress", postalCode, addressCountry: "US" },
          },
        })}</script>
        <script type="application/ld+json">{JSON.stringify(richItemListSchema(
          "Healthcare Providers Near " + (locationName || "ZIP " + postalCode),
          pageDescription,
          results,
          `/near-me/${postalCode}`,
        ))}</script>
      </Helmet>

      <div className="bg-slate-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb items={[{ name: "Home", to: "/" }, { name: "Near Me", to: "/near-me" }, { name: `ZIP ${postalCode}` }]} />
          {/* Header */}
          <div className="bg-white rounded-lg border border-slate-200 p-6 sm:p-8 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-teal/10 rounded-lg flex items-center justify-center">
                <NavigationArrow className="w-6 h-6 text-teal" weight="fill" />
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <MapPin className="w-4 h-4" />
                <span>ZIP {postalCode}</span>
              </div>
            </div>
            
            <h1 className="font-display font-bold text-3xl sm:text-4xl text-navy mb-2" data-testid="near-me-title">
              Providers Near {locationName || "You"}
            </h1>
            <p className="text-slate-500 text-lg">
              Healthcare providers within {currentRadiusLabel} of your location
            </p>
            
            {/* Filters Row */}
            <div className="mt-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center flex-wrap">
              {/* Radius Filter */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-slate-700 whitespace-nowrap">Distance:</label>
                <Select value={radiusFilter} onValueChange={handleRadiusChange}>
                  <SelectTrigger className="w-32" data-testid="near-me-radius-filter">
                    <SelectValue placeholder="10 miles" />
                  </SelectTrigger>
                  <SelectContent>
                    {RADIUS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Specialty Filter */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-slate-700 whitespace-nowrap">Specialty:</label>
                <Select value={specialtyFilter} onValueChange={handleSpecialtyChange}>
                  <SelectTrigger className="w-48" data-testid="near-me-specialty-filter">
                    <SelectValue placeholder="All Specialties" />
                  </SelectTrigger>
                  <SelectContent>
                    {POPULAR_SPECIALTIES.map((spec) => (
                      <SelectItem key={spec} value={spec}>
                        {spec}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Results count */}
              <div className="flex items-center gap-2 ml-auto">
                {loading ? (
                  <span className="text-sm text-slate-500 flex items-center gap-2">
                    <CircleNotch className="w-4 h-4 animate-spin" />
                    Searching...
                  </span>
                ) : (
                  <span className="text-sm text-teal font-medium bg-teal/10 px-3 py-1 rounded-full" data-testid="near-me-result-count">
                    {totalCount.toLocaleString()} providers found
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Results */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg border border-slate-200 p-6">
                  <div className="skeleton h-4 w-20 mb-3 rounded" />
                  <div className="skeleton h-6 w-3/4 mb-2 rounded" />
                  <div className="skeleton h-4 w-1/2 mb-2 rounded" />
                  <div className="skeleton h-4 w-1/3 mb-4 rounded" />
                  <div className="skeleton h-10 w-full rounded" />
                </div>
              ))}
            </div>
          ) : results.length === 0 ? (
            <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
              <MapPin className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="font-display font-semibold text-lg text-slate-900 mb-2">
                No providers found within {currentRadiusLabel}
              </h3>
              <p className="text-slate-500 mb-6">
                Try increasing the search radius or selecting a different specialty.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {radiusFilter !== "25" && (
                  <Button 
                    variant="outline"
                    onClick={() => handleRadiusChange("25")}
                  >
                    Expand to 25 miles
                  </Button>
                )}
                <Link to="/">
                  <Button variant="outline">
                    Search by Name or Specialty
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div 
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                data-testid="near-me-results-grid"
              >
                {results.map((provider) => (
                  <ProviderCard key={provider.number} provider={provider} />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8" data-testid="near-me-pagination">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="pagination-btn"
                  >
                    <CaretLeft className="w-4 h-4" />
                  </Button>
                  
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        className={"pagination-btn " + (currentPage === pageNum ? 'active bg-navy' : '')}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="pagination-btn"
                  >
                    <CaretRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </>
          )}
          
          {/* Info Section */}
          <div className="mt-12 bg-white rounded-lg border border-slate-200 p-6">
            <h2 className="font-display font-semibold text-xl text-slate-900 mb-4">
              About Distance Search
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Our location-based search uses your ZIP code to find nearby healthcare providers:
            </p>
            <ul className="text-slate-600 space-y-2 ml-4">
              <li className="flex items-start gap-2">
                <span className="w-2 h-2 bg-teal rounded-full mt-2 flex-shrink-0"></span>
                <span><strong>5 miles</strong> — Providers in your exact ZIP code area</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-2 h-2 bg-teal rounded-full mt-2 flex-shrink-0"></span>
                <span><strong>10 miles</strong> — Expanded search to neighboring ZIP codes</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-2 h-2 bg-teal rounded-full mt-2 flex-shrink-0"></span>
                <span><strong>25 miles</strong> — Regional search covering your broader area</span>
              </li>
            </ul>
            <p className="text-slate-500 text-sm mt-4">
              For more precise location searches, try the specialty + location search on the homepage.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default NearMePage;
